import { useDataProvider } from "@refinedev/core";

import { useTinaPoll } from "@/pages/tina/hooks/use-tina-poll";
import {
  type RefreshJobsFilterState,
  refreshJobsSummaryParams,
} from "@/pages/tina/runs/lib/refresh-jobs-filters";
import type { RefreshJobsSummary } from "@/types/tina";

interface UseRefreshRunSummaryOptions {
  /** False on a scrape or enrichment run: it fetches nothing. */
  enabled: boolean;
  /** Poll while the run can still change, on the detail query's cadence. */
  isLive: boolean;
}

/**
 * The outcome breakdown for one refresh run — `GET /refresh-jobs/summary`.
 *
 * **One request, server-aggregated, and that is the point.** The enrichment run page answers the
 * same question by fetching every row and counting client-side
 * (`collectEnrichmentRunFacts`), which is honest there — enrichment runs have a p95 of 2 items
 * and a prod max of 500. A refresh run is one hour of the nightly sweep: thousands of jobs, and
 * uncapped by default (`TINA_REFRESH_DAILY_CAP` ships at 0). `v_run_summary` cannot answer it
 * either — `created_count` and `duplicate_count` are outcomes no refresh job carries, and the
 * five `refresh_*` outcomes are invisible to `skipped_count`, which tests
 * `final_outcome='skipped'`.
 *
 * The query key carries the forwarded params, so switching the mode/status/site filter refetches
 * rather than serving a stale breakdown. The selected **outcome** is deliberately not among them
 * — see `refreshJobsSummaryParams`.
 */
export function useRefreshRunSummary(
  state: RefreshJobsFilterState,
  { enabled, isLive }: UseRefreshRunSummaryOptions,
) {
  const { custom } = useDataProvider()("tina");
  const params = refreshJobsSummaryParams(state);

  const query = useTinaPoll<RefreshJobsSummary>(
    ["tina", "refresh-jobs", "summary", params],
    async () => {
      const response = await custom!({
        url: "",
        method: "get",
        query: params,
        meta: { urlPath: "refresh-jobs/summary" },
      });
      return response.data as RefreshJobsSummary;
    },
    { enabled: enabled && Boolean(state.runId), shouldPoll: () => isLive },
  );

  return { summary: query.data ?? null, isLoading: query.isLoading };
}
